import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Fragment, useMemo, useState } from 'react'

import { PLAN_DOC as L } from '../labels'
import { shortDate } from '../lib/format'
import type { Roadmap, RoadmapEpic, RoadmapSprint, RoadmapTask } from '../lib/types'
import {
  ACCENT,
  ACCENT_SOFT,
  BAND,
  BAND_SOFT,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  serifNumber,
} from '../theme'

type Section = 'resumen' | 'ritmo' | 'arquitectura' | 'epicas' | 'sprints' | 'trazabilidad' | 'riesgos'

const SECTIONS: [Section, string][] = [
  ['resumen', L.nav.overview],
  ['ritmo', L.nav.rhythm],
  ['arquitectura', L.nav.architecture],
  ['epicas', L.nav.epics],
  ['sprints', L.nav.sprints],
  ['trazabilidad', L.nav.traceability],
  ['riesgos', L.nav.risks],
]

const OWNER_NAME: Record<string, string> = { TL: 'Lu', DS: 'Javi', SWE: 'Mike' }
const TONE: Record<string, string> = {
  vh: '#B3261E',
  h: '#A8560D',
  m: '#135A86',
  ok: '#256C4A',
  new: '#634AA0',
  l: INK_SOFT,
}

/**
 * El plan completo como documento: resumen, ritmo operativo, arquitectura, épicas,
 * sprints, trazabilidad y riesgos.
 *
 * Es de sólo lectura a propósito. Reasignar sprints vive en la pestaña Plan, donde el
 * cambio se guarda; mezclar las dos cosas haría que un documento que se lee en una junta
 * también fuera un control que se puede tocar sin querer mientras se proyecta.
 */
export function PlanDocView({ roadmap }: { roadmap: Roadmap }) {
  const [section, setSection] = useState<Section>('resumen')

  if (!roadmap.available) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography variant="h2">{L.unavailable}</Typography>
        <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 1 }}>
          {roadmap.error ?? L.unavailableHint}
        </Typography>
      </Paper>
    )
  }

  return (
    <Stack spacing={3}>
      <SectionNav value={section} onChange={setSection} />
      {section === 'resumen' && <Overview roadmap={roadmap} />}
      {section === 'ritmo' && <Rhythm roadmap={roadmap} />}
      {section === 'arquitectura' && <Architecture roadmap={roadmap} />}
      {section === 'epicas' && <Epics roadmap={roadmap} />}
      {section === 'sprints' && <Sprints roadmap={roadmap} />}
      {section === 'trazabilidad' && <Traceability roadmap={roadmap} />}
      {section === 'riesgos' && <Risks roadmap={roadmap} />}
    </Stack>
  )
}

function SectionNav({ value, onChange }: { value: Section; onChange: (s: Section) => void }) {
  return (
    <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
      {SECTIONS.map(([key, label]) => {
        const active = value === key
        return (
          <ButtonBase
            key={key}
            onClick={() => onChange(key)}
            sx={{
              px: 1.75,
              py: 0.75,
              borderRadius: 999,
              border: `1px solid ${active ? INK : LINE}`,
              bgcolor: active ? INK : 'transparent',
              color: active ? PAPER : INK_SOFT,
              fontFamily: FONT_MONO,
              fontSize: '0.6875rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              '&:hover': { bgcolor: active ? INK : BAND },
            }}
          >
            {label}
          </ButtonBase>
        )
      })}
    </Stack>
  )
}

/** Negritas y `code` en línea. El contenido es nuestro, así que el subconjunto alcanza. */
function Rich({ text, sx }: { text: string; sx?: object }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).filter(Boolean)
  return (
    <Typography sx={{ fontSize: '0.875rem', color: INK_SOFT, lineHeight: 1.65, ...sx }}>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**'))
          return (
            <Box key={i} component="strong" sx={{ color: INK, fontWeight: 600 }}>
              {p.slice(2, -2)}
            </Box>
          )
        if (p.startsWith('`') && p.endsWith('`'))
          return (
            <Box
              key={i}
              component="code"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '0.8125em',
                bgcolor: BAND,
                px: 0.5,
                py: 0.125,
                borderRadius: 0.5,
              }}
            >
              {p.slice(1, -1)}
            </Box>
          )
        if (p.startsWith('*') && p.endsWith('*'))
          return (
            <Box key={i} component="em" sx={{ fontStyle: 'italic' }}>
              {p.slice(1, -1)}
            </Box>
          )
        return <Fragment key={i}>{p}</Fragment>
      })}
    </Typography>
  )
}

function Card({
  children,
  tone,
  sx,
}: {
  children: React.ReactNode
  tone?: string
  sx?: object
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        bgcolor: PAPER,
        border: `1px solid ${LINE}`,
        borderLeft: tone ? `3px solid ${TONE[tone] ?? LINE}` : `1px solid ${LINE}`,
        ...sx,
      }}
    >
      {children}
    </Paper>
  )
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontFamily: FONT_MONO,
        fontSize: '0.625rem',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: INK_FAINT,
      }}
    >
      {children}
    </Typography>
  )
}

function Overview({ roadmap }: { roadmap: Roadmap }) {
  const c = roadmap.content.overview
  if (!c) return null
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.overview}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.overviewTitle}
        </Typography>
        <Rich text={c.lede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        }}
      >
        {c.stats.map((s: { n: string; l: string }, i: number) => (
          <Card key={i} sx={{ p: 1.75 }}>
            <Typography sx={{ ...serifNumber, fontSize: '1.5rem', color: ACCENT, lineHeight: 1.1 }}>
              {s.n}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: INK_SOFT, mt: 0.5, lineHeight: 1.45 }}>
              {s.l}
            </Typography>
          </Card>
        ))}
      </Box>

      <Card tone="m" sx={{ bgcolor: BAND_SOFT }}>
        <Rich text={c.note} />
      </Card>

      <EpicCalendar roadmap={roadmap} />

      <Box>
        <Typography sx={{ fontSize: '1.0625rem', fontWeight: 600, color: INK, mb: 1.25 }}>
          {L.decisions}
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {c.decisions.map(
            (d: { tag: string; tone: string; title: string; body: string }, i: number) => (
              <Card key={i} tone={d.tone}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.75 }}>
                  <Chip
                    size="small"
                    label={d.tag}
                    sx={{
                      height: 18,
                      fontSize: '0.625rem',
                      fontFamily: FONT_MONO,
                      bgcolor: BAND,
                      color: TONE[d.tone] ?? INK_SOFT,
                    }}
                  />
                </Stack>
                <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9375rem', mb: 0.75 }}>
                  {d.title}
                </Typography>
                <Rich text={d.body} sx={{ fontSize: '0.8125rem' }} />
              </Card>
            ),
          )}
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: '1.0625rem', fontWeight: 600, color: INK, mb: 1.25 }}>
          {L.whoDoesWhat}
        </Typography>
        <Stack spacing={1.25}>
          {c.team.map((p: Record<string, string>) => (
            <Card key={p.who}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'flex-start' }}
              >
                <Box sx={{ minWidth: 120 }}>
                  <Typography sx={{ ...serifNumber, fontSize: '1.25rem', color: INK }}>
                    {p.who}
                  </Typography>
                  <Eyebrow>{p.role}</Eyebrow>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Rich text={p.owns} sx={{ fontSize: '0.8125rem' }} />
                  <Typography
                    sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 0.75 }}
                  >
                    {p.bau}
                  </Typography>
                </Box>
                <Stack sx={{ minWidth: 130 }} spacing={0.25}>
                  <Eyebrow>{L.capacity}</Eyebrow>
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.8125rem', color: INK }}>
                    {p.committed}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
                    de {p.cap}
                  </Typography>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      <Card tone="l" sx={{ bgcolor: BAND_SOFT }}>
        <Eyebrow>{L.method}</Eyebrow>
        <Box sx={{ mt: 0.75 }}>
          <Rich text={c.method} sx={{ fontSize: '0.8125rem' }} />
        </Box>
      </Card>
    </Stack>
  )
}


const LANE_TONE: Record<string, string> = { E1: '#B3261E', E6: '#634AA0', E8: '#A8560D' }

/**
 * El calendario del plan: una columna por sprint, una fila por épica.
 *
 * Es la misma rejilla del artefacto. Responde de un vistazo la pregunta que ni la lista de
 * épicas ni la de sprints contestan sola — qué épicas comparten una quincena, y dónde una
 * épica se pausa — porque épica y sprint son ejes distintos, no una jerarquía.
 */
function EpicCalendar({ roadmap }: { roadmap: Roadmap }) {
  const { epics, sprints, tasks } = roadmap
  const active = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const t of tasks) {
      if (!t.sprint) continue
      if (!m.has(t.epic)) m.set(t.epic, new Set())
      m.get(t.epic)!.add(t.sprint)
    }
    return m
  }, [tasks])
  if (sprints.length === 0) return null
  const cols = `minmax(96px, 128px) repeat(${sprints.length}, minmax(0, 1fr))`
  return (
    <Box>
      <Typography sx={{ fontSize: '1.0625rem', fontWeight: 600, color: INK, mb: 1.25 }}>
        {L.calendarTitle}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: '3px', minWidth: 640 }}>
          <Box />
          {sprints.map((sp) => (
            <Box
              key={sp.id}
              sx={{
                px: 0.25,
                py: 0.75,
                borderRadius: '5px',
                textAlign: 'center',
                bgcolor: BAND_SOFT,
                border: `1px solid ${sp.risk === 'HIGH' ? TONE.vh : LINE}`,
                borderStyle: sp.kind === 'holiday' ? 'dashed' : 'solid',
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: sp.risk === 'HIGH' ? TONE.vh : INK,
                  lineHeight: 1.1,
                }}
              >
                {sp.id}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.5625rem',
                  color: INK_FAINT,
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                }}
              >
                {shortDate(sp.start)}
              </Typography>
            </Box>
          ))}
          {epics.map((e) => (
            <Fragment key={e.id}>
              <Box
                title={e.name}
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.5875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: INK_FAINT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {e.id} {e.short ?? ''}
              </Box>
              {sprints.map((sp) => {
                const on = active.get(e.id)?.has(sp.id) ?? false
                const tone = LANE_TONE[e.id] ?? ACCENT
                return (
                  <Box
                    key={sp.id}
                    title={on ? `${e.id} · ${sp.id}` : undefined}
                    sx={{
                      minHeight: 18,
                      borderRadius: '3px',
                      bgcolor: on ? tone : BAND_SOFT,
                      border: `1px solid ${on ? tone : LINE}`,
                      opacity: on || sp.kind !== 'holiday' ? 1 : 0.45,
                    }}
                  />
                )
              })}
            </Fragment>
          ))}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: INK_FAINT, mt: 1 }}>
        {L.calendarHint}
      </Typography>
    </Box>
  )
}

function Rhythm({ roadmap }: { roadmap: Roadmap }) {
  const c = roadmap.content.rhythm
  if (!c) return null
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.rhythm}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.rhythmTitle}
        </Typography>
        <Rich text={c.lede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>
      <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {c.cards.map((k: Record<string, string>, i: number) => (
          <Card key={i} tone={k.tone}>
            <Chip
              size="small"
              label={k.cadence}
              sx={{
                height: 18,
                fontSize: '0.625rem',
                fontFamily: FONT_MONO,
                bgcolor: BAND,
                color: TONE[k.tone] ?? INK_SOFT,
                mb: 1,
              }}
            />
            <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9375rem', mb: 0.75 }}>
              {k.title}
            </Typography>
            <Rich text={k.body} sx={{ fontSize: '0.8125rem' }} />
            {k.extra && (
              <Typography
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: '0.6875rem',
                  color: INK_FAINT,
                  mt: 1,
                  whiteSpace: 'pre-line',
                }}
              >
                {k.extra}
              </Typography>
            )}
          </Card>
        ))}
      </Box>
      <Stack spacing={1.25}>
        {c.notes.map((n: string, i: number) => (
          <Card key={i} tone="h" sx={{ bgcolor: BAND_SOFT }}>
            <Rich text={n} sx={{ fontSize: '0.8125rem' }} />
          </Card>
        ))}
      </Stack>
    </Stack>
  )
}

function Architecture({ roadmap }: { roadmap: Roadmap }) {
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.architecture}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.archTitle}
        </Typography>
        <Rich text={L.archLede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>
      {roadmap.figures.map((f) => (
        <Card key={f.id}>
          <Eyebrow>
            {L.figure} {f.num} — {f.title}
          </Eyebrow>
          <Box
            sx={{
              mt: 1.25,
              mb: 1.25,
              overflowX: 'auto',
              color: INK,
              '& svg': { width: '100%', minWidth: 720, height: 'auto', display: 'block' },
            }}
            // Contenido propio, versionado junto al plan. No hay entrada de usuario aquí.
            dangerouslySetInnerHTML={{ __html: f.svg }}
          />
          <Rich text={f.caption} sx={{ fontSize: '0.8125rem' }} />
        </Card>
      ))}
    </Stack>
  )
}

function Epics({ roadmap }: { roadmap: Roadmap }) {
  const tasksOf = (id: string) => roadmap.tasks.filter((t) => t.epic === id)
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.epics}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.epicsTitle}
        </Typography>
        <Rich text={L.epicsLede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>
      <Stack spacing={1.25}>
        {roadmap.epics.map((e) => (
          <EpicBlock key={e.id} epic={e} tasks={tasksOf(e.id)} sprints={roadmap.sprints} />
        ))}
      </Stack>
    </Stack>
  )
}

function EpicBlock({
  epic,
  tasks,
  sprints,
}: {
  epic: RoadmapEpic
  tasks: RoadmapTask[]
  sprints: RoadmapSprint[]
}) {
  const order = new Map(sprints.map((s, i) => [s.id, i]))
  const active = [...new Set(tasks.map((t) => t.sprint).filter(Boolean) as string[])].sort(
    (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0),
  )
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: PAPER, border: `1px solid ${LINE}`, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack sx={{ width: '100%', pr: 1 }} spacing={0.5}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Box component="span" sx={{ ...serifNumber, fontSize: '1.125rem', color: ACCENT }}>
              {epic.id}
            </Box>
            <Typography sx={{ fontWeight: 600, color: INK }}>{epic.name}</Typography>
            <Box sx={{ flex: 1 }} />
            <Chip
              size="small"
              label={`${epic.inhorizon} ${L.inHorizon}`}
              sx={{ height: 18, fontSize: '0.625rem' }}
            />
            {epic.beyond > 0 && (
              <Chip
                size="small"
                label={`${epic.beyond} ${L.beyond}`}
                sx={{ height: 18, fontSize: '0.625rem', bgcolor: BAND }}
              />
            )}
          </Stack>
          <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT }}>{epic.oneliner}</Typography>
          {active.length > 0 && (
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {active.join(' · ')}
              {epic.finish ? ` — ${L.closesOn} ${epic.finish.label}` : ''}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.75}>
          <Field label={L.problem} text={epic.problem} />
          <Field label={L.why} text={epic.why} />
          <Field label={L.outcome} text={epic.outcome} />
          {(epic.scope ?? []).length > 0 && <ListField label={L.scope} items={epic.scope!} />}
          {(epic.exclusions ?? []).length > 0 && (
            <ListField label={L.exclusions} items={epic.exclusions!} tone="l" />
          )}
          {(epic.success ?? []).length > 0 && (
            <ListField label={L.success} items={epic.success!} tone="ok" />
          )}
          {(epic.deps ?? []).length > 0 && <ListField label={L.deps} items={epic.deps!} tone="h" />}
          <Field label={L.components} text={(epic.components ?? []).join(' · ')} mono />
          <Divider />
          <EpicTimeline epic={epic} />
          <Divider />
          <Box>
            <Eyebrow>
              {L.includedItems} ({tasks.length})
            </Eyebrow>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {tasks.map((t) => (
                <TaskLine key={t.id} task={t} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}


/**
 * El recorrido de una épica: en qué sprints entrega, con qué items, y cuándo cierra.
 *
 * Una épica no ocupa sprints contiguos — se pausa cuando la ventana de refresh se lleva
 * al equipo y retoma después. Los huecos se nombran en vez de dejar que la tabla sugiera
 * trabajo continuo.
 */
function EpicTimeline({ epic }: { epic: RoadmapEpic }) {
  const rows = epic.bysprint ?? []
  if (rows.length === 0 || !epic.finish) {
    return (
      <Box>
        <Eyebrow>{L.sprintBySprint}</Eyebrow>
        <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT, mt: 0.75 }}>
          {L.nothingScheduled}
        </Typography>
      </Box>
    )
  }
  return (
    <Box>
      <Eyebrow>{L.sprintBySprint}</Eyebrow>
      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 0.5 }}>
        {L.opens} {epic.finish.start_label} · {L.closes} {epic.finish.label} ·{' '}
        {epic.pdtotal ?? 0} {L.inHorizonDays}
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {rows.map((r) => (
          <Box
            key={r.sprint}
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: '1fr', sm: '104px 1fr 56px' },
              alignItems: 'start',
              py: 0.75,
              borderTop: `1px solid ${LINE}`,
            }}
          >
            <Box>
              <Typography
                sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: ACCENT, fontWeight: 600 }}
              >
                {r.sprint}
              </Typography>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT }}>
                {r.window}
              </Typography>
              {r.kind !== 'normal' && (
                <Typography sx={{ fontSize: '0.625rem', color: TONE[r.kind === 'refresh' ? 'vh' : 'l'] }}>
                  {r.kind === 'refresh' ? L.refreshWindow : L.protected}
                </Typography>
              )}
            </Box>
            <Stack spacing={0.25}>
              {r.tasks.map((t) => (
                <Stack key={t.id} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                  <Box
                    component="span"
                    sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}
                  >
                    {t.id}
                  </Box>
                  <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT, flex: 1, minWidth: 140 }}>
                    {t.title}
                  </Typography>
                  <Box
                    component="span"
                    sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT }}
                  >
                    {OWNER_NAME[t.owner] ?? t.owner} · {t.pdays} pd
                  </Box>
                </Stack>
              ))}
            </Stack>
            <Typography
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '0.75rem',
                color: INK,
                textAlign: { sm: 'right' },
              }}
            >
              {r.pd} pd
            </Typography>
          </Box>
        ))}
      </Stack>
      {(epic.gaps ?? []).length > 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: INK_FAINT, mt: 1 }}>
          {L.dormantIn} {epic.gaps!.join(', ')} — {L.dormantHint}
        </Typography>
      )}
    </Box>
  )
}

function Field({ label, text, mono }: { label: string; text?: string; mono?: boolean }) {
  if (!text) return null
  return (
    <Box>
      <Eyebrow>{label}</Eyebrow>
      <Box sx={{ mt: 0.5 }}>
        {mono ? (
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_SOFT }}>
            {text}
          </Typography>
        ) : (
          <Rich text={text} sx={{ fontSize: '0.8125rem' }} />
        )}
      </Box>
    </Box>
  )
}

function TaskLine({ task }: { task: RoadmapTask }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 1, py: 0.5, borderRadius: 1, border: `1px solid ${LINE}` }}
    >
      <Box
        component="span"
        sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_SOFT, width: 62 }}
      >
        {task.id}
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', color: INK, flex: 1, minWidth: 0 }} noWrap>
        {task.title}
      </Typography>
      {task.owner && (
        <Chip
          size="small"
          label={OWNER_NAME[task.owner] ?? task.owner}
          sx={{ height: 17, fontSize: '0.5625rem' }}
        />
      )}
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.625rem',
          color: task.sprint ? INK_FAINT : ACCENT,
          width: 52,
          textAlign: 'right',
        }}
      >
        {task.sprint ?? L.outOfHorizon}
      </Box>
    </Stack>
  )
}

function Sprints({ roadmap }: { roadmap: Roadmap }) {
  const load = useMemo(() => new Map(roadmap.load.map((l) => [l.sprint_id, l])), [roadmap.load])
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.sprints}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.sprintsTitle}
        </Typography>
      </Box>
      <Stack spacing={1.25}>
        {roadmap.sprints.map((s) => (
          <SprintDoc
            key={s.id}
            sprint={s}
            tasks={roadmap.tasks.filter((t) => t.sprint === s.id)}
            load={load.get(s.id)}
          />
        ))}
      </Stack>
    </Stack>
  )
}

function SprintDoc({
  sprint,
  tasks,
  load,
}: {
  sprint: RoadmapSprint
  tasks: RoadmapTask[]
  load?: { committed: Record<string, number>; capacity: Record<string, number> }
}) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: PAPER, border: `1px solid ${LINE}`, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack sx={{ width: '100%', pr: 1 }} spacing={0.5}>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
            <Box component="span" sx={{ ...serifNumber, fontSize: '1.125rem', color: INK }}>
              {sprint.id}
            </Box>
            <Typography sx={{ fontWeight: 600, color: INK }}>{sprint.name}</Typography>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {shortDate(sprint.start)} – {shortDate(sprint.end)}
            </Typography>
            {sprint.kind !== 'normal' && (
              <Chip
                size="small"
                label={sprint.kind === 'refresh' ? L.refreshWindow : L.protected}
                sx={{ height: 18, fontSize: '0.625rem', bgcolor: BAND }}
              />
            )}
            {sprint.risk === 'HIGH' && (
              <Chip
                size="small"
                label={L.highRisk}
                sx={{ height: 18, fontSize: '0.625rem', bgcolor: ACCENT_SOFT, color: ACCENT }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {tasks.length} {L.tasks}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: '0.8125rem', color: INK_SOFT }}>{sprint.goal}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.75}>
          {sprint.description && <Field label={L.why} text={sprint.description} />}
          {load && (
            <Box>
              <Eyebrow>{L.budget}</Eyebrow>
              <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                {(['TL', 'DS', 'SWE'] as const).map((o) => {
                  const cap = load.capacity[o] ?? 0
                  const used = load.committed[o] ?? 0
                  if (!cap) return null
                  return (
                    <Typography
                      key={o}
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: '0.75rem',
                        color: used > cap ? ACCENT : INK_SOFT,
                      }}
                    >
                      {OWNER_NAME[o]} {used}/{cap}
                    </Typography>
                  )
                })}
              </Stack>
            </Box>
          )}
          {sprint.capnote && <Field label={L.capacityNote} text={sprint.capnote} />}
          {!!sprint.deliverables?.length && (
            <ListField label={L.deliverables} items={sprint.deliverables} />
          )}
          {!!sprint.risks?.length && <ListField label={L.risks} items={sprint.risks} tone="vh" />}
          {sprint.endstate && <Field label={L.endstate} text={sprint.endstate} />}
          {sprint.parallel && <Field label={L.parallel} text={sprint.parallel} />}
          <Divider />
          <Box>
            <Eyebrow>
              {L.tasks} ({tasks.length})
            </Eyebrow>
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {tasks.map((t) => (
                <TaskDetail key={t.id} task={t} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function ListField({ label, items, tone }: { label: string; items: string[]; tone?: string }) {
  return (
    <Box>
      <Eyebrow>{label}</Eyebrow>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.75, pl: 2.25 }}>
        {items.map((it, i) => (
          <Box
            key={i}
            component="li"
            sx={{ '&::marker': { color: tone ? TONE[tone] : INK_FAINT } }}
          >
            <Rich text={it} sx={{ fontSize: '0.8125rem' }} />
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function TaskDetail({ task }: { task: RoadmapTask }) {
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: BAND_SOFT, border: `1px solid ${LINE}`, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ minHeight: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
          <Box
            component="span"
            sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_SOFT, width: 62 }}
          >
            {task.id}
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: INK, flex: 1, minWidth: 0 }} noWrap>
            {task.title}
          </Typography>
          {task.priority && (
            <Chip size="small" label={task.priority} sx={{ height: 17, fontSize: '0.5625rem' }} />
          )}
          {task.owner && (
            <Chip
              size="small"
              label={OWNER_NAME[task.owner] ?? task.owner}
              sx={{ height: 17, fontSize: '0.5625rem' }}
            />
          )}
          <Box
            component="span"
            sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT, width: 40, textAlign: 'right' }}
          >
            {task.pdays} d
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: PAPER }}>
        <Stack spacing={1.5}>
          <Field label={L.description} text={task.description} />
          {task.approach && <Field label={L.approach} text={task.approach} />}
          {task.sizing && <Field label={`${L.sizing} — ${task.pdays} d`} text={task.sizing} />}
          {task.evidence && <Field label={L.evidence} text={task.evidence} />}
          {task.rationale && <Field label={L.rationale} text={task.rationale} />}
          {!!task.acceptance?.length && <ListField label={L.acceptance} items={task.acceptance} />}
          {!!task.components?.length && (
            <Field label={L.components} text={task.components.join(' · ')} mono />
          )}
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
              {L.excelPriority}: {task.origPriority} → {task.priority}
            </Typography>
            {task.deps.length > 0 && (
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT }}>
                {L.dependsOn}: {task.deps.join(', ')}
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function Traceability({ roadmap }: { roadmap: Roadmap }) {
  const rows = useMemo(
    () => [...roadmap.tasks].sort((a, b) => a.id.localeCompare(b.id)),
    [roadmap.tasks],
  )
  return (
    <Stack spacing={2}>
      <Box>
        <Eyebrow>{L.nav.traceability}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.traceTitle}
        </Typography>
        <Rich text={L.traceLede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>
      <Card sx={{ p: 0, overflowX: 'auto' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 740 }}>
          <Box component="thead">
            <Box component="tr">
              {[L.item, L.excel, L.recommended, L.epic, L.sprint, L.owner, L.decision].map((h) => (
                <Box
                  key={h}
                  component="th"
                  sx={{
                    textAlign: 'left',
                    p: 1.25,
                    fontFamily: FONT_MONO,
                    fontSize: '0.625rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: INK_FAINT,
                    borderBottom: `1px solid ${LINE}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((t) => (
              <Box key={t.id} component="tr" sx={{ '&:hover': { bgcolor: BAND_SOFT } }}>
                <Td mono>{t.id}</Td>
                <Td>{t.origPriority}</Td>
                <Td>{t.priority ?? '—'}</Td>
                <Td mono>{t.epic}</Td>
                <Td mono accent={!t.sprint}>
                  {t.sprint ?? '—'}
                </Td>
                <Td>{t.owner ? (OWNER_NAME[t.owner] ?? t.owner) : '—'}</Td>
                <Td small>{t.decision}</Td>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>
    </Stack>
  )
}

function Td({
  children,
  mono,
  small,
  accent,
}: {
  children: React.ReactNode
  mono?: boolean
  small?: boolean
  accent?: boolean
}) {
  return (
    <Box
      component="td"
      sx={{
        p: 1.25,
        borderBottom: `1px solid ${LINE}`,
        fontFamily: mono ? FONT_MONO : undefined,
        fontSize: small ? '0.75rem' : '0.8125rem',
        color: accent ? ACCENT : INK_SOFT,
        verticalAlign: 'top',
      }}
    >
      {children}
    </Box>
  )
}

function Risks({ roadmap }: { roadmap: Roadmap }) {
  const c = roadmap.content.risks
  if (!c) return null
  return (
    <Stack spacing={2.5}>
      <Box>
        <Eyebrow>{L.nav.risks}</Eyebrow>
        <Typography sx={{ ...serifNumber, fontSize: '1.75rem', color: INK, mt: 0.5, mb: 1.25 }}>
          {L.risksTitle}
        </Typography>
        <Rich text={c.lede} sx={{ fontSize: '0.9375rem', maxWidth: '76ch' }} />
      </Box>

      <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {c.cards.map((r: Record<string, string>, i: number) => (
          <Card key={i} tone={r.tone}>
            <Chip
              size="small"
              label={r.tag}
              sx={{
                height: 18,
                fontSize: '0.625rem',
                fontFamily: FONT_MONO,
                bgcolor: BAND,
                color: TONE[r.tone] ?? INK_SOFT,
                mb: 1,
              }}
            />
            <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9375rem', mb: 0.75 }}>
              {r.title}
            </Typography>
            <Rich text={r.body} sx={{ fontSize: '0.8125rem' }} />
          </Card>
        ))}
      </Box>

      <Box>
        <Typography sx={{ fontSize: '1.0625rem', fontWeight: 600, color: INK, mb: 1.25 }}>
          {L.externalDeps}
        </Typography>
        <Stack spacing={1}>
          {c.deps.map((d: Record<string, string>, i: number) => (
            <Card key={i}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Box sx={{ minWidth: 200 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: INK }}>
                    {d.what}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: FONT_MONO, fontSize: '0.6875rem', color: INK_FAINT, mt: 0.25 }}
                  >
                    {L.blocks} {d.blocks} · {d.by}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Rich text={d.state} sx={{ fontSize: '0.8125rem' }} />
                </Box>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography sx={{ fontSize: '1.0625rem', fontWeight: 600, color: INK, mb: 1.25 }}>
          {L.openQuestions}
        </Typography>
        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {c.questions.map((q: Record<string, string>, i: number) => (
            <Card key={i} tone="new">
              <Chip
                size="small"
                label={q.tag}
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontFamily: FONT_MONO,
                  bgcolor: BAND,
                  color: TONE.new,
                  mb: 1,
                }}
              />
              <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9375rem', mb: 0.75 }}>
                {q.title}
              </Typography>
              <Rich text={q.body} sx={{ fontSize: '0.8125rem' }} />
            </Card>
          ))}
        </Box>
      </Box>
    </Stack>
  )
}
